import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useFetchData } from 'services/api/useFetchData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { availableIntegrations } from './availableIntegrations';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import IntegrationPage from './IntegrationPage';
import '../../../style/scss/settings.scss';

interface CurrentIntegration {
    id: number,
    slug: string,
    status: string
};

const IntegrationsLibraryPage: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const integrationSlug = queryParams.get("integration");
    const integrationId = queryParams.get("id");
    const { response, loading } = useFetchData({ apiUrl: 'get_integration_list' });
    const [currentIntegrations, setCurrentIntegrations] = useState<CurrentIntegration[]>([]);
    const activeSlugs = new Set(currentIntegrations.map(i => i.slug));

    // Filter the available integrations
    const filteredAvailableIntegrations = availableIntegrations.filter(
        integration => !activeSlugs.has(integration.slug)
    );

    // Set the current integrations
    useEffect(() => {
        if (response) {
            setCurrentIntegrations(response);
        };
    }, [response]);

    // Get the data of the available integrations, based on the slug of the current integrations
    const enrichedCurrentIntegrations = currentIntegrations.map(currentIntegration => {
        const availableIntegrationData = availableIntegrations.find(
            integration => integration.slug === currentIntegration.slug
        );

        if (!availableIntegrationData) return null;

        return {
            ...currentIntegration,
            ...availableIntegrationData
        };
    }).filter(Boolean);

    // If an integration is selected, show detail view instead of library
    if (integrationSlug) {

        // Get the integrationData
		const integrationData = availableIntegrations.find(i => i.slug === integrationSlug);

		return (
			<div className='integration-page container-filled'>
				<div className='page-header row'>
					<div className='header-title'>
						<span className='back-button' 
                              onClick={() => history.push(location.pathname)}>
							<FontAwesomeIcon icon={faAngleLeft} />
						</span>
						<h3>
							{integrationData?.name}
						</h3>
					</div>
				</div>

				<IntegrationPage 
                    slug={integrationSlug}
                    id={integrationId ? Number(integrationId) : undefined} 
                />
			</div>
		);
	}

    // Click handler for integrations
    const handleClick = (integration: any, isCurrent: boolean) => {
        const idPart = isCurrent ? `&id=${integration.id}` : "";
        history.push(`${location.pathname}?integration=${integration.slug}${idPart}`);
    };

    // Render an integration library card
    const renderIntegrationCard = (integration: any, isCurrent: boolean) => {

        // Determine the right button label
        const buttonLabel = isCurrent
            ? 'integrations.general.see_status_label'
            : 'integrations.general.more_info_label';

        return (
            <div className="library-card" 
                 key={integration.slug}
                 onClick={() => handleClick(integration, isCurrent)}>
                <div className='logo'>
                    <img src={integration.logo} alt={integration.name} />
                </div>
                <div className='title'>
                    <h5>{integration.name}</h5>
                </div>
                <div className='description'>
                    <p>{t(integration.summary)}</p>
                </div>
                <div className='button'>
                    <PrimaryButton 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClick(integration, isCurrent);
                        }}
                        label={buttonLabel}
                        size="extra-small"
                    />
                </div>
            </div>
        );
    };
    
    return (
        <div className='integrations-library container-filled'>
            {loading === 'show-loader' ? (
                <div className='inline-form-loader'>
                    <div className="loader"></div>
                </div>
            ) : loading === 'success' ? (
                <div className='market-place-app-library'>
                    
                    {enrichedCurrentIntegrations.length > 0 && (
                        <div className='library-group'>
                            <h3>{t('integrations.general.current_integrations_header')}</h3>
                            <div className='library-cards'>
                                {enrichedCurrentIntegrations.map(integration =>
                                    renderIntegrationCard(integration, true)
                                )}
                            </div>
                        </div>
                    )}

                    <div className='library-group'>
                        <h3>{t('integrations.general.available_integrations_header')}</h3>
                        <div className='library-cards'>
                            {filteredAvailableIntegrations.map(integration =>
                                renderIntegrationCard(integration, false)
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
};

export default IntegrationsLibraryPage;